import { useContext, useState } from "react";
import {
  DialogContent,
  DialogActions,
  Divider,
  Typography,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import DragModal from "../../common/components/DragModal";
import { convertUDWithTagDefaultsToDTO } from "../helpers/Helpers";
import { LookupValueContext } from "../../common/contexts/LookupValueContext";

const UniversalDescriptorEditor = ({
  open,
  onClose,
  universalDescriptor,
  onSave,
}) => {
  const lookupValues = useContext(LookupValueContext);

  const getLookupOptions = (category) => {
    for (let i = 0; i < lookupValues.length; i++) {
      if (lookupValues[i].LookupName === category) {
        return lookupValues[i].Values;
      }
    }
    return [];
  };

  const [formData, setFormData] = useState({
    UniversalDescriptorId: universalDescriptor?.UniversalDescriptorId || 0,
    UD: universalDescriptor?.UD || "",
    Description: universalDescriptor?.Description || "",
    IsActive: universalDescriptor?.IsActive ?? true,
    TagName: universalDescriptor?.TagName || "",
    TagIdentifier: universalDescriptor?.TagDefaults?.TagIdentifier ?? null,
    DisplayName: universalDescriptor?.TagDefaults?.DisplayName || "",
    MaxValue: universalDescriptor?.TagDefaults?.MaxValue ?? null,
    MinValue: universalDescriptor?.TagDefaults?.MinValue ?? null,
    GroupName: universalDescriptor?.TagDefaults?.GroupName || "",
    AlarmLoLo: universalDescriptor?.TagDefaults?.AlarmLoLo ?? null,
    AlarmLo: universalDescriptor?.TagDefaults?.AlarmLo ?? null,
    AlarmHiHi: universalDescriptor?.TagDefaults?.AlarmHiHi ?? null,
    AlarmHi: universalDescriptor?.TagDefaults?.AlarmHi ?? null,
    Gain: universalDescriptor?.TagDefaults?.Gain ?? null,
    Offset: universalDescriptor?.TagDefaults?.Offset ?? null,
    UnitOfMeasure: universalDescriptor?.TagDefaults?.UnitOfMeasure || "",
    DisplayFormat: universalDescriptor?.TagDefaults?.DisplayFormat || "",
    SourceTypeId: universalDescriptor?.TagDefaults?.SourceTypeId ?? null,
    DynamicProperties:
      universalDescriptor?.TagDefaults?.DynamicProperties || "",
    DeviceParams: universalDescriptor?.TagDefaults?.DeviceParams || "",
    ModbusMap: universalDescriptor?.ModbusMap || null,
    UpdatedBy: universalDescriptor?.UpdatedBy || "",
    UpdatedDate: universalDescriptor?.UpdatedDate || null,
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNumberChange = (field, value) => {
    if (value === "") {
      setFormData((prev) => ({
        ...prev,
        [field]: null,
      }));
    } else {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue)) {
        setFormData((prev) => ({
          ...prev,
          [field]: numValue,
        }));
      }
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave(convertUDWithTagDefaultsToDTO(formData));
    }
    onClose();
  };

  return (
    <DragModal
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='lg'
      titlecomponent={
        <Typography variant='h5' color='primary'>
          {formData.UniversalDescriptorId === 0 ? "Create" : "Edit"} Universal
          Descriptor
        </Typography>
      }>
      <Divider />
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant='subtitle2' sx={{ mt: 1 }}>
              Basic Information
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label='Universal Descriptor (UD)'
              value={formData.UD}
              onChange={(e) => handleChange("UD", e.target.value)}
              disabled={formData.UniversalDescriptorId !== 0}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label='Tag Name'
              value={formData.TagName}
              onChange={(e) => handleChange("TagName", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label='Description'
              value={formData.Description}
              onChange={(e) => handleChange("Description", e.target.value)}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.IsActive}
                  onChange={(e) => handleChange("IsActive", e.target.checked)}
                />
              }
              label='Is Active'
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Modbus Map'
              value={formData.ModbusMap}
              type='number'
              onChange={(e) => handleNumberChange("ModbusMap", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Display Name'
              value={formData.DisplayName}
              onChange={(e) => handleChange("DisplayName", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Group Name'
              value={formData.GroupName}
              onChange={(e) => handleChange("GroupName", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Unit of Measure'
              value={formData.UnitOfMeasure}
              onChange={(e) => handleChange("UnitOfMeasure", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Display Format'
              value={formData.DisplayFormat}
              onChange={(e) => handleChange("DisplayFormat", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant='subtitle2' sx={{ mt: 1 }}>
              Value Range
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type='number'
              label='Min Value'
              value={formData.MinValue ?? ""}
              onChange={(e) => handleNumberChange("MinValue", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type='number'
              label='Max Value'
              value={formData.MaxValue ?? ""}
              onChange={(e) => handleNumberChange("MaxValue", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant='subtitle2' sx={{ mt: 1 }}>
              Default Alarm Limits
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type='number'
              label='Alarm LoLo'
              value={formData.AlarmLoLo ?? ""}
              onChange={(e) => handleNumberChange("AlarmLoLo", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type='number'
              label='Alarm Lo'
              value={formData.AlarmLo ?? ""}
              onChange={(e) => handleNumberChange("AlarmLo", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type='number'
              label='Alarm Hi'
              value={formData.AlarmHi ?? ""}
              onChange={(e) => handleNumberChange("AlarmHi", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type='number'
              label='Alarm HiHi'
              value={formData.AlarmHiHi ?? ""}
              onChange={(e) => handleNumberChange("AlarmHiHi", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant='subtitle2' sx={{ mt: 1 }}>
              Data Transformation
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type='number'
              label='Gain'
              value={formData.Gain ?? ""}
              onChange={(e) => handleNumberChange("Gain", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type='number'
              label='Offset'
              value={formData.Offset ?? ""}
              onChange={(e) => handleNumberChange("Offset", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant='subtitle2' sx={{ mt: 1 }}>
              Other Properties
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type='number'
              label='Tag Identifier'
              value={formData.TagIdentifier ?? ""}
              onChange={(e) =>
                handleNumberChange("TagIdentifier", e.target.value)
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Source Type</InputLabel>
              <Select
                value={formData.SourceTypeId}
                onChange={(e) =>
                  handleNumberChange("SourceTypeId", e.target.value)
                }
                label='Source Type'>
                {getLookupOptions("TagSourceType").map((option) => (
                  <MenuItem
                    key={option.LookupValueId}
                    value={option.LookupValueId}>
                    {option.DisplayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Dynamic Properties'
              value={formData.DynamicProperties}
              onChange={(e) =>
                handleChange("DynamicProperties", e.target.value)
              }
              multiline
              rows={3}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Device Params'
              value={formData.DeviceParams}
              onChange={(e) => handleChange("DeviceParams", e.target.value)}
              multiline
              rows={3}
            />
          </Grid>
          {formData.UniversalDescriptorId !== 0 && (
            <>
              <Grid item xs={12}>
                <Typography variant='subtitle2' sx={{ mt: 1 }}>
                  Audit Information
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label='Updated By'
                  value={formData.UpdatedBy}
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label='Updated Date'
                  value={
                    formData.UpdatedDate
                      ? new Date(formData.UpdatedDate).toLocaleString()
                      : ""
                  }
                  disabled
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant='contained'
          startIcon={<SaveIcon />}>
          Save
        </Button>
      </DialogActions>
    </DragModal>
  );
};

export default UniversalDescriptorEditor;
